import { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

export function Select({ placeholder, options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleSelect = useCallback(
    (option) => {
      onChange(option);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleSelectClick = useCallback(
    (e) => {
      handleSelect(e.currentTarget.dataset.value);
    },
    [handleSelect]
  );

  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange(null);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <Container ref={containerRef}>
      <SelectInput
        data-open={isOpen}
        data-value={!!value}
        onClick={handleToggle}
      >
        <SelectValue data-value={!!value}>{value || placeholder}</SelectValue>
        <IconWrapper>
          {value && !isOpen ? (
            <ClearIcon onClick={handleClear}>✕</ClearIcon>
          ) : (
            <ChevronIcon data-open={isOpen} />
          )}
        </IconWrapper>
      </SelectInput>

      {isOpen && (
        <OptionsList>
          {options.map((option) => (
            <Option
              key={option}
              data-selected={option === value}
              data-value={option}
              onClick={handleSelectClick}
            >
              {option}
            </Option>
          ))}
        </OptionsList>
      )}
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SelectInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 40px;
  background: #263750;
  border: 1px solid #83bf46;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
  user-select: none;

  &:hover {
    background: #334466;
  }

  &[data-open='true'] {
    background: #334466;
  }
`;

const SelectValue = styled.span`
  color: #b3b3b3;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[data-value='true'] {
    color: #ffffff;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  flex-shrink: 0;
`;

const ChevronIcon = styled.span`
  width: 8px;
  height: 8px;
  border-right: 2px solid #b2b2b2;
  border-bottom: 2px solid #b2b2b2;
  transform: rotate(45deg);
  transition: transform 0.2s, border-color 0.2s;
  margin-bottom: 4px;

  ${SelectInput}:hover & {
    border-color: #ffffff;
  }

  &[data-open='true'] {
    border-color: #ffffff;
    transform: rotate(-135deg);
    margin-bottom: -4px;
  }
`;

const ClearIcon = styled.span`
  color: #f5f5f5;
  font-size: 14px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: color 0.2s, border 0.2s;

  &:hover {
    color: #83bf46;
  }
`;

const OptionsList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);

  &::-webkit-scrollbar {
    width: 4px;
    margin-right: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin-top: 8px;
    margin-right: 4px;
  }
`;

const Option = styled.div`
  padding: 10px 16px;
  color: #1e1e1e;
  cursor: pointer;
  transition: background 0.15s;

  &[data-selected='true'] {
    font-weight: 700;
  }

  &:hover {
    background: #83bf4633;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;
